import { useCallback, useEffect, useMemo, useRef } from 'react';
const AUTOSAVE_DEBOUNCE_MS = 800;
/**
 * The subset of the document the save actually persists (see `saveDocumentToStorage`). Keying
 * autosave on this instead of on the document object is what stops a non-persisted mutation from
 * writing — `SET_DOCUMENT_PDF` produces a new document on every open of a template whose PDF
 * isn't cached yet.
 */
const persistedSnapshot = ({ elements, roles, totalPages, language, delayedSigning, customFields, name, description, instructionScreen, completionScreen, carryForward, sourceLabel, sourceVersion, sourceVersionDate, sourceFileName, }) => JSON.stringify({
    elements,
    roles,
    totalPages,
    language,
    delayedSigning,
    customFields,
    name,
    description,
    instructionScreen,
    completionScreen,
    carryForward,
    sourceLabel,
    sourceVersion,
    sourceVersionDate,
    sourceFileName,
});
/**
 * Debounced autosave for the consent editor, which has no Save button.
 *
 * Only a DRAFT resource accepts a `data` payload — Library's FSM rejects one against
 * IN_REVIEW/PUBLISHED/ARCHIVED with `library.conflict.status` — so scheduling is gated on both
 * status and the caller's suspend flag rather than left to fire whenever an edit lands.
 */
export const useConsentAutosave = ({ document, isSuspended, save, onSaveError, }) => {
    const timeoutRef = useRef();
    /** The edit waiting on the debounce timer, kept so a close/switch can still rescue it. */
    const pendingRef = useRef(null);
    const inFlightCountRef = useRef(0);
    /**
     * Saves run one at a time through this chain. Library replaces `Resource.data` wholesale, so
     * two overlapping PUTs would let a slow older request land last and revert the newer edit.
     */
    const queueRef = useRef(Promise.resolve());
    const savedRef = useRef(null);
    const saveRef = useRef(save);
    saveRef.current = save;
    const onSaveErrorRef = useRef(onSaveError);
    onSaveErrorRef.current = onSaveError;
    const snapshot = useMemo(() => (document ? persistedSnapshot(document) : null), [document]);
    const dispatchSave = useCallback((documentToSave) => {
        pendingRef.current = null;
        inFlightCountRef.current += 1;
        queueRef.current = queueRef.current.then(async () => {
            try {
                await saveRef.current(documentToSave);
            }
            catch (error) {
                onSaveErrorRef.current(error, documentToSave);
            }
            finally {
                inFlightCountRef.current -= 1;
            }
        });
    }, []);
    useEffect(() => {
        if (!document || snapshot === null) {
            savedRef.current = null;
            return;
        }
        // Opening a template adopts its stored state as the baseline, so opening one never writes.
        if (savedRef.current?.documentId !== document.id) {
            savedRef.current = { documentId: document.id, snapshot };
            return;
        }
        if (isSuspended || document.status !== 'Draft' || snapshot === savedRef.current.snapshot) {
            return;
        }
        pendingRef.current = document;
        timeoutRef.current = setTimeout(() => {
            // Marked before the save resolves: a failure notifies instead of retrying silently, and
            // the next edit re-dirties the document anyway.
            savedRef.current = { documentId: document.id, snapshot };
            dispatchSave(document);
        }, AUTOSAVE_DEBOUNCE_MS);
        // Disarms the timer only — the edit stays in `pendingRef`. Re-running on each subsequent
        // edit is what produces the debounce.
        return () => clearTimeout(timeoutRef.current);
    }, [document, snapshot, isSuspended, dispatchSave]);
    const documentId = document?.id ?? null;
    useEffect(() => () => {
        // The editor modal is never unmounted: closing sets the active document to null and
        // switching templates swaps its id. This cleanup is therefore the only place an edit still
        // inside the debounce window can be rescued rather than dropped.
        clearTimeout(timeoutRef.current);
        if (pendingRef.current) {
            dispatchSave(pendingRef.current);
        }
    }, [documentId, dispatchSave]);
    useEffect(() => {
        const warnWhenUnsaved = (event) => {
            if (!pendingRef.current && inFlightCountRef.current === 0) {
                return;
            }
            // A hard navigation can't be flushed — the save is an authenticated PUT, so there's no
            // sendBeacon path. Prompting is the only honest guard for the debounce window.
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnWhenUnsaved);
        return () => window.removeEventListener('beforeunload', warnWhenUnsaved);
    }, []);
    const settlePendingSaves = useCallback(async () => {
        clearTimeout(timeoutRef.current);
        pendingRef.current = null;
        await queueRef.current;
    }, []);
    return { settlePendingSaves };
};
